import { z } from 'zod';

import { CustomerDeliveryChannelSchema } from './customer-delivery-channel.schema.js';
import { ReceptionBelongingsSnapshotSchema } from './reception-belongings.schemas.js';
import { ReceptionInspectionChecklistItemSchema } from './reception-inspection-catalog.js';
import { ReceptionTermSnapshotSchema } from './reception-term.schemas.js';

export const ReceptionChecklistItemSchema = ReceptionInspectionChecklistItemSchema;

export const UpdateReceptionCaptureBodySchema = z
  .object({
    checklistSnapshot: z.array(ReceptionChecklistItemSchema).nullable().optional(),
    fuelLevelEighths: z.number().int().min(0).max(8).nullable().optional(),
    estimatedDeliveryAt: z.iso.datetime().nullable().optional(),
    belongingsSnapshot: ReceptionBelongingsSnapshotSchema.nullable().optional(),
    termSnapshot: ReceptionTermSnapshotSchema.nullable().optional(),
    entryOdometerKm: z.number().int().nonnegative().nullable().optional(),
    reportedIssue: z.string().trim().max(2000).nullable().optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
    /** When true, mark awaiting titular approval and notify the customer (BR-SO-80). Autosave omits this. */
    sendToTitularApp: z.boolean().optional(),
    /** App or WhatsApp. Omitted keeps the current fan-out. */
    deliveryChannel: CustomerDeliveryChannelSchema.optional(),
    /** BR-SO-118 / BR-SO-141 — swap the OPEN check-in vehicle (and derived customer). */
    customerVehicleId: z.uuid().optional(),
    partyId: z.uuid().optional(),
    customerRoleId: z.uuid().optional(),
  })
  .superRefine((body, ctx) => {
    const hasVehicle = body.customerVehicleId !== undefined;
    const hasParty = body.partyId !== undefined;
    const hasRole = body.customerRoleId !== undefined;
    if (hasVehicle === hasParty && hasVehicle === hasRole) {
      return;
    }
    ctx.addIssue({
      code: 'custom',
      message: 'partyId, customerRoleId and customerVehicleId must be sent together',
      path: ['customerVehicleId'],
    });
  });

export type UpdateReceptionCaptureBody = z.infer<typeof UpdateReceptionCaptureBodySchema>;

export const UpdateReceptionCaptureResponseSchema = z.object({
  receptionId: z.uuid(),
});

export type UpdateReceptionCaptureResponse = z.infer<typeof UpdateReceptionCaptureResponseSchema>;

export const AddReceptionPhotoBodySchema = z.object({
  storageUrl: z.string().trim().min(1).max(500),
  caption: z.string().max(500).nullable().optional(),
});

export type AddReceptionPhotoBody = z.infer<typeof AddReceptionPhotoBodySchema>;

export const AddReceptionPhotoResponseSchema = z.object({
  photoId: z.uuid(),
});

export type AddReceptionPhotoResponse = z.infer<typeof AddReceptionPhotoResponseSchema>;

export const SignReceptionTermBodySchema = z.object({
  signedByName: z.string().trim().min(1).max(200),
  storageUrl: z.string().trim().min(1).max(500),
});

export type SignReceptionTermBody = z.infer<typeof SignReceptionTermBodySchema>;

export const SignReceptionTermResponseSchema = z.object({
  signatureId: z.uuid(),
});

export type SignReceptionTermResponse = z.infer<typeof SignReceptionTermResponseSchema>;

export const CompleteReceptionCaptureResponseSchema = z.object({
  receptionId: z.uuid(),
  status: z.enum(['OPEN', 'COMPLETED', 'CANCELLED']),
});

export const CancelOpenReceptionResponseSchema = z.object({
  receptionId: z.uuid(),
  status: z.literal('CANCELLED'),
  quoteId: z.uuid().nullable(),
});

export type CancelOpenReceptionResponse = z.infer<typeof CancelOpenReceptionResponseSchema>;

export type CompleteReceptionCaptureResponse = z.infer<
  typeof CompleteReceptionCaptureResponseSchema
>;
